import { useQuery } from "@tanstack/react-query"
import { isAxiosError } from "axios"

import { getRateConStatusFn } from "@/api/tenderApi"
import { IOrderStrategy, IRateConStatusDto } from "@/shared/types"
import {
  shouldFetchRateConStatus,
  shouldPollRateConStatus,
  showToast,
} from "@/shared/utils"

const RATE_CON_POLL_INTERVAL_MS = 45_000
const RATE_CON_DEFAULT_INTERVAL_MS = 30_000

export const useRateConStatus = (
  orgId: string,
  originalOrderId: string,
  strategy?: IOrderStrategy,
  isStrategyLoading = false,
) => {
  const isEnabled =
    !!orgId && !!originalOrderId && !isStrategyLoading && shouldFetchRateConStatus(strategy)

  return useQuery(
    ["rateConStatus", orgId, originalOrderId],
    async (): Promise<IRateConStatusDto | null> => {
      const status = await getRateConStatusFn(originalOrderId, orgId)
      if (!status.integrationEnabled) {
        return null
      }
      return status
    },
    {
      staleTime: 30 * 1000,
      cacheTime: isEnabled ? 30 * 1000 : 0,
      refetchInterval: (data) =>
        shouldPollRateConStatus(data) ? RATE_CON_POLL_INTERVAL_MS : RATE_CON_DEFAULT_INTERVAL_MS,
      keepPreviousData: true,
      enabled: isEnabled,
      retry: (failureCount, error) => {
        if (isAxiosError(error) && error.response?.status === 404) {
          return false
        }
        return failureCount < 2
      },
      onError: (error) => {
        if (isAxiosError(error) && error.response?.status === 404) {
          return
        }
        if (isAxiosError(error)) {
          showToast({ type: "error", text: error.response?.data.errorMessage || error.message })
        }
      },
    },
  )
}
